import { CollectStatus } from '~/types/graphql-backend-types/gql-types'

export function useCollect() {
  const {
    isAdmin,
    isProducer,
    isRecycler,
  } = storeToRefs(useAuthStore())
  const { t } = useI18n()

  const statusDefinitions = computed(() => {
    const baseStatuses = [
      { value: CollectStatus.RequestSent, comment: t('global.awaiting_recycler_validation'), label: t('global.request_sent') },
      { value: CollectStatus.TreatmentInProgress, comment: t('collect.collect_in_progress'), label: t('collect.status_treatmentInProgress') },
      { value: CollectStatus.Treated, comment: isAdmin.value ? t('global.documentation_being_finalized') : t('collect.data_complete'), label: t('collect.data_complete') },
      { value: CollectStatus.Confirmed, comment: t('global.upcoming service'), label: t('collect.status_confirmed') },
      { value: CollectStatus.Finished, comment: t('collect.collect_validated_and_closed'), label: t('collect.data_complete') },
      { value: CollectStatus.DateConflict, comment: t('collect.status_dateConflict'), label: t('collect.status_dateConflict') },
      { value: CollectStatus.HasIncident, comment: t('global.problems_with_the_service'), label: t('collect.status_hasIncident') },
      { value: CollectStatus.Cancelled, comment: t('collect.collect_cancelled_and_not_completed'), label: t('collect.status_cancelled') },
    ]

    // Filter statuses for producers to hide internal statuses
    if (isProducer.value && !isAdmin.value) {
      return baseStatuses.filter(s =>
        s.value !== CollectStatus.Invoicing &&
        s.value !== CollectStatus.Finished,
      )
    }

    // For admins, include Invoicing
    if (isAdmin.value) {
      return [
        ...baseStatuses,
        { value: CollectStatus.Invoicing, comment: t('collect.status_invoicing'), label: t('collect.data_complete') },
      ]
    }

    return baseStatuses
  })

  // Unified status labels for all user types
  function statusLabelByUserRole(status: CollectStatus) {
    // Group completed statuses (treated, invoicing, finished) as "Données complètes" for all users
    if (status === CollectStatus.Invoicing ||
        status === CollectStatus.Treated ||
        status === CollectStatus.Finished) {
      return `${t('collect.data_complete')}`
    }

    if (isAdmin.value)
      return `${t(`collect.status_${status}`)}`

    if (isRecycler.value && status === CollectStatus.RequestSent) {
      return `${t('collect.pending_collect')}`
    }

    return `${t(`collect.status_${status}`)}`
  }

  function availableStatuses(oldStatus: CollectStatus) {
    const availableStatuses: { value: CollectStatus, label: string, disabled?: boolean }[] = []

    if (isAdmin.value) {
      availableStatuses.push(
        { value: CollectStatus.RequestSent, label: t('global.request_sent') },
        { value: CollectStatus.TreatmentInProgress, label: t('collect.status_treatmentInProgress') },
        { value: CollectStatus.Treated, label: t('collect.data_complete') },
        { value: CollectStatus.Confirmed, label: t('collect.status_confirmed') },
        { value: CollectStatus.Invoicing, label: t('collect.data_complete') },
        { value: CollectStatus.HasIncident, label: t('collect.status_hasIncident') },
        { value: CollectStatus.Cancelled, label: t('collect.status_cancelled') },
        { value: CollectStatus.Finished, label: t('collect.data_complete') },
      )
    }
    else if (isProducer.value) {
      switch (oldStatus) {
        case CollectStatus.RequestSent:
          availableStatuses.push(
            { value: CollectStatus.HasIncident, label: t('collect.status_hasIncident') },
            { value: CollectStatus.Cancelled, label: t('collect.status_cancelled') },
          )
          break
        case CollectStatus.Confirmed:
          availableStatuses.push({ value: CollectStatus.HasIncident, label: t('collect.status_hasIncident') })
          break
      }
    }
    else if (isRecycler.value) {
      switch (oldStatus) {
        case CollectStatus.RequestSent:
          availableStatuses.push(
            { value: CollectStatus.Confirmed, label: t('collect.status_confirmed') },
            { value: CollectStatus.HasIncident, label: t('collect.status_hasIncident') },
            { value: CollectStatus.Cancelled, label: t('collect.status_cancelled') },
          )
          break
        case CollectStatus.Confirmed:
          availableStatuses.push(
            { value: CollectStatus.HasIncident, label: t('collect.status_hasIncident') },
          )
          break
        case CollectStatus.TreatmentInProgress:
          availableStatuses.push(
            { value: CollectStatus.Treated, label: t('collect.data_complete') },
            { value: CollectStatus.HasIncident, label: t('collect.status_hasIncident') },
          )
          break
      }
    }

    return availableStatuses
  }

  function getStatusDefinition(status: CollectStatus) {
    return statusDefinitions.value.find(s => s.value === status)?.comment
  }

  function getResolveIncidentStatuses() {
    const statuses: Array<{
      value: CollectStatus
      label: string
    }> = []

    statuses.push(
      {
        value: CollectStatus.TreatmentInProgress,
        label: t('collect.status_treatmentInProgress'),
      },
      {
        value: CollectStatus.Treated,
        label: t('collect.data_complete'),
      },
    )

    return statuses
  }

  // Check if status represents completed collection (data complete)
  // Used for filtering and UI logic
  function isCompletedStatusForProducer(status: CollectStatus): boolean {
    return status === CollectStatus.Treated ||
           status === CollectStatus.Invoicing ||
           status === CollectStatus.Finished
  }

  // Check if status is "data complete" (same logic, clearer name)
  function isDataCompleteStatus(status: CollectStatus): boolean {
    return isCompletedStatusForProducer(status)
  }

  // Get unique status definitions by label (for filters)
  // Returns first status value for each unique label
  const uniqueStatusDefinitions = computed(() => {
    const seenLabels = new Set<string>()
    const unique: Array<{ value: CollectStatus; label: string; tooltip?: string }> = []

    for (const statusDef of statusDefinitions.value) {
      const label = statusLabelByUserRole(statusDef.value)
      if (!seenLabels.has(label)) {
        seenLabels.add(label)
        unique.push({
          value: statusDef.value,
          label,
          tooltip: statusDef.comment,
        })
      }
    }

    return unique
  })

  return {
    availableStatuses,
    getStatusDefinition,
    statusLabelByUserRole,
    statusDefinitions,
    uniqueStatusDefinitions,
    getResolveIncidentStatuses,
    isCompletedStatusForProducer,
    isDataCompleteStatus,
  }
}
